import { useRef } from 'react';
import type {
  FC,
  CSSProperties,
  PropsWithChildren,
  VideoHTMLAttributes,
  HTMLAttributes,
  DetailedHTMLProps,
  LegacyRef,
  ImgHTMLAttributes,
} from 'react';
import { useFirstInView } from '@fuxi/eevee-hooks';
import type { Options } from 'ahooks/lib/useInViewport';
import { DEFAULT_IMG } from '../../constants';

export type LazyProps<T extends unknown = never> = FC<
  {
    src: string;
    className?: string;
    style?: CSSProperties;
    inViewOptions?: Options;
    widthDefaultImg?: boolean;
  } & T
>;

const LazyDiv: FC<PropsWithChildren<HTMLAttributes<HTMLDivElement>>> = props => {
  const { className, children, style, onClick } = props;

  const ref = useRef<HTMLDivElement | null>(null);
  const inView = useFirstInView(ref);

  return (
    <div className={className} style={style} ref={ref} onClick={onClick}>
      {inView && children}
    </div>
  );
};

const LazyImg: LazyProps<ImgHTMLAttributes<HTMLImageElement>> = props => {
  const { className, src, alt = '', style, widthDefaultImg = false, inViewOptions } = props;

  const ref = useRef<HTMLImageElement | null>(null);
  const inView = useFirstInView(ref, inViewOptions);

  return (
    <img
      className={className}
      style={style}
      ref={ref}
      src={inView ? src : widthDefaultImg ? DEFAULT_IMG : ''}
      alt={alt}
    />
  );
};

const LazyVideo: LazyProps<
  {
    videoRef?: LegacyRef<HTMLVideoElement>;
    wrapperStyle?: CSSProperties;
  } & DetailedHTMLProps<VideoHTMLAttributes<HTMLVideoElement>, HTMLVideoElement>
> = props => {
  const { className, wrapperStyle = {}, src, style, inViewOptions, videoRef, poster, ...rest } = props;

  const ref = useRef<HTMLDivElement | null>(null);
  const inView = useFirstInView(ref, inViewOptions);

  return (
    <div style={wrapperStyle} ref={ref}>
      <video
        ref={videoRef}
        className={className}
        style={style}
        src={inView ? src : DEFAULT_IMG}
        poster={poster ?? DEFAULT_IMG}
        {...rest}></video>
    </div>
  );
};

export { LazyImg, LazyVideo, LazyDiv };
